import { SQLFilters } from '../../../../api/types';
import {
  ChartGridConfig,
  LanguageString,
  PageConfig,
  StoryboardComponentDependencies,
} from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import { DataTypes, EmployeeDataFields } from '../../../../types';
import { getLastMonthEndDate } from '../../../../utils-date';
import { gridCols, gridRows } from '../chart-layouts';
import {
  getHeadcountsByDimensionChartConfig,
  getHeadcountsTopChartConfig,
  getNikkiAgeFactbox,
  getNikkiGenderRatioFactbox,
  getNikkiHeadcountFactbox,
  getNikkiTenureFactbox,
} from './headcount-chart-helpers';

export const getNikkiHeadcountDbPageConfig = (pageOption: {
  pageId: number;
  dashboardId: string;
  title: LanguageString;
  filters: SQLFilters;
}): PageConfig => {
  const { pageId, dashboardId, title, filters } = pageOption;
  const firstChartDimension =
    pageId === 1
      ? {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
        }
      : {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ORGANIZATION_LEVEL_2,
        };
  const numBottomCharts = pageId === 2 ? 6 : 5;
  const bottomChartRowConfig = {
    gridRowStart: 12,
    gridRowEnd: 17,
  };
  const bottomChartGridConfigs: ChartGridConfig[] =
    numBottomCharts === 5
      ? [
          { gridColStart: 1, gridColEnd: 15, ...bottomChartRowConfig },
          { gridColStart: 15, gridColEnd: 26, ...bottomChartRowConfig },
          { gridColStart: 26, gridColEnd: 40, ...bottomChartRowConfig },
          { gridColStart: 40, gridColEnd: 54, ...bottomChartRowConfig },
          { gridColStart: 54, gridColEnd: 79, ...bottomChartRowConfig },
        ]
      : [
          { gridColStart: 1, gridColEnd: 12, ...bottomChartRowConfig },
          { gridColStart: 12, gridColEnd: 20, ...bottomChartRowConfig },
          { gridColStart: 20, gridColEnd: 32, ...bottomChartRowConfig },
          { gridColStart: 32, gridColEnd: 47, ...bottomChartRowConfig },
          { gridColStart: 47, gridColEnd: 60, ...bottomChartRowConfig },
          { gridColStart: 60, gridColEnd: 79, ...bottomChartRowConfig },
        ];
  const byDimensionChartConfigs = [
    {
      dimension: firstChartDimension,
      title: {
        [Languages.EN]: 'Organization',
        [Languages.JA]: 'Organization',
      },
      gridConfig: {
        gridColStart: 1,
        gridColEnd: 30,
        gridRowStart: 7,
        gridRowEnd: 12,
      },
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
      },
      title: {
        [Languages.EN]: 'Function',
        [Languages.JA]: 'Function',
      },
      gridConfig: {
        gridColStart: 30,
        gridColEnd: 79,
        gridRowStart: 7,
        gridRowEnd: 12,
      },
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.TENURE_GROUP,
      },
      title: {
        [Languages.EN]: 'Tenure Group',
        [Languages.JA]: 'Tenure Group',
      },
      gridConfig: bottomChartGridConfigs[0],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.GENDER,
      },
      title: {
        [Languages.EN]: 'Gender',
        [Languages.JA]: 'Gender',
      },
      gridConfig: bottomChartGridConfigs[1],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.NATIONALITY_HIERARCHICAL_LEVEL_1,
      },
      title: {
        [Languages.EN]: 'Nationality',
        [Languages.JA]: 'Nationality',
      },
      gridConfig: bottomChartGridConfigs[2],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.AGE_GROUP,
      },
      title: {
        [Languages.EN]: 'Age Group',
        [Languages.JA]: 'Age Group',
      },
      gridConfig: bottomChartGridConfigs[3],
    },
    {
      dimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.CUSTOM_FIELD_3_LEVEL_1,
      },
      title: {
        [Languages.EN]: 'Global Grade',
        [Languages.JA]: 'Global Grade',
      },
      gridConfig: bottomChartGridConfigs[4],
    },
    ...(pageId === 2
      ? [
          {
            dimension: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
            },
            title: {
              [Languages.EN]: 'Job Grade',
              [Languages.JA]: 'Job Grade',
            },
            gridConfig: bottomChartGridConfigs[5],
          },
        ]
      : []),
  ];

  const pageComponents = ({
    storyboardDateUtils,
  }: StoryboardComponentDependencies) => {
    return [
      getHeadcountsTopChartConfig(storyboardDateUtils, filters),
      getNikkiHeadcountFactbox(storyboardDateUtils, filters),
      getNikkiTenureFactbox(storyboardDateUtils, filters),
      getNikkiGenderRatioFactbox(storyboardDateUtils, filters),
      getNikkiAgeFactbox(storyboardDateUtils, filters),
      ...byDimensionChartConfigs.map((chartConfig) =>
        getHeadcountsByDimensionChartConfig(
          storyboardDateUtils,
          chartConfig.dimension,
          chartConfig.title,
          filters,
          chartConfig.gridConfig
        )
      ),
    ];
  };
  const pageConfig: PageConfig = {
    pageId: pageId.toString(),
    dashboardId,
    pageNumber: pageId,
    enabled: true,
    title,
    pageOptions: {
      displayOptions: {
        enabled: true,
      },
      timeSlider: {
        enabled: false,
      },
      filterTray: {
        enabled: false,
      },
      dataView: {
        enabled: false,
      },
      segmentationLevel1: {
        enabled: false,
      },
      segmentationLevel2: {
        enabled: false,
      },
      benchmark: {
        enabled: false,
      },
      forecast: {
        enabled: false,
      },
    },
    gridCols: gridCols,
    gridRows: gridRows,
    components: pageComponents,
    referenceDate: getLastMonthEndDate(),
  };
  return pageConfig;
};

const jgblYocFilters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_YOC'`;
const jgblJphilFilters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JPHIL'`;
const jgblJindFilters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JIND'`;
const jgblJgcapFilters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JGCAP'`;
const jgblJicFilters = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JIC'`;

export const getNikkiHeadcountDbPageConfigs = (
  dashboardId: string
): PageConfig[] => {
  const pageOptions = [
    {
      pageId: 1,
      filters: '',
      title: {
        [Languages.EN]: 'YOC & ALL OC  Headcount',
        [Languages.JA]: 'YOC & ALL OC  Headcount',
      },
    },
    {
      pageId: 2,
      filters: jgblYocFilters,
      title: {
        [Languages.EN]: 'YOC Headcount',
        [Languages.JA]: 'YOC Headcount',
      },
    },
    {
      pageId: 3,
      filters: jgblJphilFilters,
      title: {
        [Languages.EN]: 'JPHIL Headcount',
        [Languages.JA]: 'JPHIL Headcount',
      },
    },
    {
      pageId: 4,
      filters: jgblJindFilters,
      title: {
        [Languages.EN]: 'JIND Headcount',
        [Languages.JA]: 'JIND Headcount',
      },
    },
    {
      pageId: 5,
      filters: jgblJgcapFilters,
      title: {
        [Languages.EN]: 'JGCAP Headcount',
        [Languages.JA]: 'JGCAP Headcount',
      },
    },
    {
      pageId: 6,
      filters: jgblJicFilters,
      title: {
        [Languages.EN]: 'JIC Headcount',
        [Languages.JA]: 'JIC Headcount',
      },
    },
  ];
  const pageConfigs = pageOptions.map((pageOption) =>
    getNikkiHeadcountDbPageConfig({
      ...pageOption,
      dashboardId,
    })
  );
  return pageConfigs;
};
